import { getTenantUnits } from '@core/configuration';
import {
  FormikProject,
  type ProductsByPlantFormikType,
  type ProjectFormikType,
} from '@core/formik/project';
import { CalculateInitialPrice, buildInitialDeliveryCosts } from '@core/hooks/useProductSection';
import type Enums from '@core/types/api/Enums';
import type { ProjectConfig } from '@core/types/api/ProjectConfig/ProjectConfig';
import type { ProjectConfigProduct } from '@core/types/api/ProjectConfigProduct/ProjectConfigProduct';
import { NewProjectPlant } from '@core/types/api/ProjectPlant/ProjectPlant';
import { NewProjectProduct } from '@core/types/api/ProjectProduct/ProjectProduct';
import type { TenantDefaultMargins } from '@core/types/tenantDefaultMargins';
import { ZERO_DURATION_STRING } from '@core/utils/dateHelpers';
import { hasValue } from '@core/utils/equality';
import { mergeProductsByPlantsFormik } from '@core/utils/projectHelpers';

type ProjectConfigFormikValues = Pick<
  ProjectFormikType,
  | 'readyMixQuantityOverride'
  | 'readyMixQuantityOverrideUnit'
  | 'revenueOverride'
  | 'contractors'
  | 'otherCompanies'
  | 'productsByPlants'
>;

type BuildProjectConfigFormikValuesParams = {
  projectConfig: ProjectConfig;
  projectConfigProducts: ProjectConfigProduct[];
  currentProductsByPlants: ProductsByPlantFormikType[];
  shouldMergeProducts: boolean;
  usesDispatchCustomer: boolean;
  defaultToSuggestedPrice: boolean;
  defaultIsMoneySwitchOn: boolean;
  defaultMargins: TenantDefaultMargins;
  marginConfiguration: Enums.MarginConfiguration;
};

/** The project form fields a selected project config overwrites. */
export const buildProjectConfigFormikValues = ({
  projectConfig,
  projectConfigProducts,
  currentProductsByPlants,
  shouldMergeProducts,
  usesDispatchCustomer,
  defaultToSuggestedPrice,
  defaultIsMoneySwitchOn,
  defaultMargins,
  marginConfiguration,
}: BuildProjectConfigFormikValuesParams): ProjectConfigFormikValues => {
  const configProject = FormikProject(
    {
      readyMixQuantityOverride: projectConfig.readyMixQuantityOverride,
      revenueOverride: projectConfig.revenueOverride,
      companies: projectConfig.companies,
      products: projectConfigProducts.map((pcp) => {
        // Price the product with the drive distance already known for its plant.
        const currentPlant = currentProductsByPlants.find(
          (p) => p.plant.id === pcp.product.plant.id,
        );
        const deliveryCosts = buildInitialDeliveryCosts({
          deliveryCosts: pcp.product.plant.deliveryCosts,
          drivingTime: currentPlant?.plantDriveTime ?? ZERO_DURATION_STRING,
          drivingDistance: currentPlant?.plantDistance ?? null,
        });

        return NewProjectProduct({
          product: pcp.product,
          quantity: pcp.quantity,
          externalName: pcp.externalName,
          usage: pcp.usage,
          deliveryCosts,
          price: CalculateInitialPrice({
            plantProduct: pcp.product,
            projectProduct: undefined,
            deliveryCosts,
            defaultToSuggestedPrice,
            marginConfiguration,
            defaultMargins,
          }),
        });
      }),
      plants: projectConfig.plants.map((plant) =>
        NewProjectPlant({
          plant: { id: plant.id, name: plant.name },
          plantDistance: null,
          plantDistanceUnit: null,
          plantDriveTime: null,
        }),
      ),
    },
    // Custom fields are not overwritten, so their definitions are not needed.
    [],
    usesDispatchCustomer,
    defaultIsMoneySwitchOn,
    defaultMargins,
    marginConfiguration,
    true,
  );

  const hasReadyMixOverride = hasValue(configProject.readyMixQuantityOverride);

  return {
    readyMixQuantityOverride: configProject.readyMixQuantityOverride,
    readyMixQuantityOverrideUnit:
      projectConfig.readyMixQuantityOverrideUnit ??
      (hasReadyMixOverride ? getTenantUnits().dryVolume : null),
    revenueOverride: configProject.revenueOverride,
    contractors: configProject.contractors,
    otherCompanies: configProject.otherCompanies,
    productsByPlants: shouldMergeProducts
      ? mergeProductsByPlantsFormik(currentProductsByPlants, configProject.productsByPlants)
      : configProject.productsByPlants,
  };
};
